import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  GraduationCap,
  FileText,
  Clock,
  Upload,
  Loader2,
  Download,
  CheckCircle,
  Inbox,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { admissionsAPI } from '../../services/api';

const STATUS_COLORS = {
  'Assigned': 'bg-blue-100 text-blue-700',
  'Docs Pending': 'bg-amber-100 text-amber-700',
  'Docs Verified': 'bg-cyan-100 text-cyan-700',
  'Submitted': 'bg-indigo-100 text-indigo-700',
  'Under Review': 'bg-purple-100 text-purple-700',
  'Offer Received': 'bg-emerald-100 text-emerald-700',
  'Accepted': 'bg-green-100 text-green-700',
  'Visa Processing': 'bg-orange-100 text-orange-700',
  'Completed': 'bg-green-200 text-green-800',
  'Rejected': 'bg-red-100 text-red-700'
};

export default function StudentAdmissions() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [acceptingOffer, setAcceptingOffer] = useState(null);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const result = await admissionsAPI.getStudentList({ limit: 50 });
      setApplications(result.data || []);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleUpload = async (appId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('docName', file.name);
      await admissionsAPI.studentUploadDoc(appId, formData);
      toast.success('Document uploaded');
      fetchApplications();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAcceptOffer = async (appId) => {
    setAcceptingOffer(appId);
    try {
      await admissionsAPI.acceptOffer(appId);
      toast.success('Offer accepted successfully!');
      fetchApplications();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to accept offer');
    } finally {
      setAcceptingOffer(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-outfit font-bold text-slate-900">My Applications</h1>
          <p className="text-slate-500 mt-1">Track your university applications and upload documents</p>
        </div>

        {applications.length === 0 ? (
          <Card className="p-12 bg-white rounded-xl border border-slate-100 shadow-sm text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700">No Applications Yet</h3>
            <p className="text-slate-500 mt-1">Your agent or admin will create applications for you.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => {
              const isExpanded = expandedId === app.applicationId;
              return (
                <Card key={app.applicationId} className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                  {/* Summary Row */}
                  <div
                    className="p-5 cursor-pointer hover:bg-slate-50/50 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : app.applicationId)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/20">
                          <GraduationCap className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{app.universityName}</h3>
                          <p className="text-sm text-slate-500">{app.programName} - {app.intake}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={`rounded-full ${STATUS_COLORS[app.status] || 'bg-slate-100 text-slate-700'}`}>
                          {app.status}
                        </Badge>
                        {app.status === 'Offer Received' && (
                          <Button
                            size="sm"
                            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 shadow-md shadow-emerald-500/20 transition-all duration-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptOffer(app.applicationId);
                            }}
                            disabled={acceptingOffer === app.applicationId}
                          >
                            {acceptingOffer === app.applicationId ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-1" />
                            )}
                            Accept Offer
                          </Button>
                        )}
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>Agent: {app.agentName}</span>
                      <span>Updated: {new Date(app.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 p-5">
                      <Tabs defaultValue="documents">
                        <TabsList className="mb-4 bg-slate-100 p-1 rounded-xl">
                          <TabsTrigger value="documents" className="flex items-center gap-1.5 rounded-lg">
                            <FileText className="w-3.5 h-3.5" /> Documents
                          </TabsTrigger>
                          <TabsTrigger value="timeline" className="flex items-center gap-1.5 rounded-lg">
                            <Clock className="w-3.5 h-3.5" /> Timeline
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="documents">
                          <div className="space-y-3">
                            {/* Upload */}
                            <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                              <input
                                type="file"
                                id={`student-upload-${app.applicationId}`}
                                className="hidden"
                                onChange={(e) => handleUpload(app.applicationId, e)}
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-xl border-slate-200 hover:border-sky-200 hover:bg-sky-50/50 transition-all duration-200"
                                onClick={() => document.getElementById(`student-upload-${app.applicationId}`).click()}
                                disabled={uploading}
                              >
                                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                                Upload Document
                              </Button>
                            </div>

                            {app.documents?.length > 0 ? (
                              app.documents.map((doc) => (
                                <div key={doc.docId} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-xl">
                                  <div className="flex items-center gap-3">
                                    <FileText className="w-4 h-4 text-sky-600" />
                                    <div>
                                      <p className="text-sm font-medium text-slate-800">{doc.name}</p>
                                      <p className="text-xs text-slate-400">
                                        {new Date(doc.uploadedAt).toLocaleDateString()} by {doc.uploadedByRole}
                                      </p>
                                    </div>
                                  </div>
                                  <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                    <Download className="w-4 h-4 text-slate-500 hover:text-sky-600 transition-colors" />
                                  </a>
                                </div>
                              ))
                            ) : (
                              <p className="text-sm text-slate-500 text-center py-3">No documents yet.</p>
                            )}
                          </div>
                        </TabsContent>

                        <TabsContent value="timeline">
                          {app.timeline?.length > 0 ? (
                            <div className="space-y-3">
                              {[...app.timeline].reverse().map((entry, idx) => (
                                <div key={idx} className="flex gap-3">
                                  <div className="w-6 h-6 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Clock className="w-3 h-3 text-sky-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm text-slate-700">{entry.action}</p>
                                    <p className="text-xs text-slate-400">{new Date(entry.date).toLocaleString()}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 text-center py-3">No timeline entries.</p>
                          )}
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
